import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Home, Mail, CheckCircle, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './ui/carousel';

export const LodgesCombinsPage = () => {
  const navigate = useNavigate();

  const images = [
    "https://customer-assets.emergentagent.com/job_7231506c-99db-422c-800b-87878e7e456c/artifacts/xstv99av_IMMOCONCEPT_RIEME_COMBINS_EXT_JOUR_HD.jpg",
    "https://customer-assets.emergentagent.com/job_7231506c-99db-422c-800b-87878e7e456c/artifacts/o3ivueef_IMMOCONCEPT_RIEME_COMBINS_EXT_NUIT_HD.jpg",
    "https://customer-assets.emergentagent.com/job_7231506c-99db-422c-800b-87878e7e456c/artifacts/hiumqzdg_IMMOCONCEPT_RIEME_COMBINS_TERRASSE_HD.jpg",
    "https://customer-assets.emergentagent.com/job_alpine-invest-3/artifacts/aevcux8v_ChatGPT%20Image%2017%20fe%CC%81vr.%202026%2C%2014_38_08.png",
    "https://customer-assets.emergentagent.com/job_alpine-invest-3/artifacts/w68lm10f_ChatGPT%20Image%2017%20fe%CC%81vr.%202026%2C%2014_19_55.png",
    "https://customer-assets.emergentagent.com/job_alpine-invest-3/artifacts/g0cvf83j_ChatGPT%20Image%2014%20fe%CC%81vr.%202026%2C%2009_39_17.png"
  ];

  const caracteristiques = [
    { label: "Type", value: "Résidence touristique" },
    { label: "Localisation", value: "Grimentz, Val d'Anniviers" },
    { label: "Chalets", value: "5 indépendants" },
    { label: "Appartements 3.5p", value: "6 unités" },
    { label: "Chalets 5.5p", value: "2 unités" },
    { label: "Surface", value: "78-228 m²" },
    { label: "Rendement cible", value: "3-4%" },
    { label: "Usage propriétaire", value: "6 semaines/an" }
  ];

  const atouts = [
    "Vue panoramique exceptionnelle",
    "Architecture chalet moderne",
    "SPA & fitness premium",
    "Gestion locative professionnelle",
    "Performance énergétique PAC air/eau",
    "Parking couvert",
    "Local à skis",
    "Position dominante et calme",
    "Positionnement premium 4 étoiles",
    "Vente en PPE par lot",
    "Usage propriétaire encadré (3 semaines hiver + 3 semaines été)",
    "Accessible aux acquéreurs étrangers"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1e5a7a] text-white py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-white hover:text-[#f5b800] mb-6"
          >
            <ArrowLeft className="mr-2" size={20} />
            Retour à l'accueil
          </Button>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Lodges des Combins
          </h1>
          <div className="flex flex-wrap gap-4 text-lg">
            <div className="flex items-center">
              <MapPin className="mr-2" size={20} />
              Grimentz, Val d'Anniviers
            </div>
            <div className="flex items-center">
              <Home className="mr-2" size={20} />
              Résidence touristique premium
            </div>
            <div className="flex items-center">
              <TrendingUp className="mr-2" size={20} />
              Rendement 3-4%
            </div>
          </div>
        </div>
      </div>

      {/* Carrousel d'images */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <Carousel className="w-full">
            <CarouselContent>
              {images.map((image, index) => (
                <CarouselItem key={index}>
                  <div className="relative h-[500px] md:h-[600px] overflow-hidden rounded-2xl">
                    <img
                      src={image}
                      alt={`Lodges des Combins - Image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-4" />
            <CarouselNext className="right-4" />
          </Carousel>
        </div>
      </section>

      {/* Description */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Colonne principale */}
            <div className="lg:col-span-2 space-y-8">
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-[#1e5a7a] mb-6">
                    Investissement Immobilier d'Exception
                  </h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-4">
                    Au cœur du Val d'Anniviers, Grimentz incarne l'âme préservée des Alpes suisses. Village classé parmi les plus beaux de Suisse, il conjugue patrimoine valaisan, domaine skiable d'altitude et attractivité touristique annuelle.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed mb-4">
                    Le hameau <strong>Lodges des Combins</strong> propose des appartements neufs à visée locative, avec tout le confort des services hôteliers et la liberté d'en profiter plusieurs semaines par an.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    Cette organisation garantit cohérence, image homogène et optimisation du taux d'occupation.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-[#1e5a7a] mb-6">
                    Une Résidence Structurée
                  </h2>
                  <p className="text-lg text-gray-700 mb-6">
                    Découvrez une opportunité d'investissement clé en main : des appartements entièrement meublés et prêts à la location.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-[#1e5a7a]/5 p-6 rounded-xl text-center">
                      <p className="text-4xl font-bold text-[#f5b800] mb-2">5</p>
                      <p className="text-gray-700">Chalets indépendants</p>
                    </div>
                    <div className="bg-[#1e5a7a]/5 p-6 rounded-xl text-center">
                      <p className="text-4xl font-bold text-[#f5b800] mb-2">6</p>
                      <p className="text-gray-700">Appartements 3.5 pièces</p>
                    </div>
                    <div className="bg-[#1e5a7a]/5 p-6 rounded-xl text-center">
                      <p className="text-4xl font-bold text-[#f5b800] mb-2">2</p>
                      <p className="text-gray-700">Chalets 5.5 pièces</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-[#1e5a7a] mb-6">
                    Les Atouts du Projet
                  </h2>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {atouts.map((atout, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle size={20} className="text-[#f5b800] mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{atout}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="border-0 shadow-xl bg-gradient-to-br from-[#1e5a7a] to-[#15475c] text-white sticky top-8">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6 text-[#f5b800]">
                    Informations Clés
                  </h3>
                  <div className="space-y-4 mb-8">
                    {caracteristiques.map((item, index) => (
                      <div key={index} className="border-b border-white/20 pb-3">
                        <p className="text-sm text-white/70 mb-1">{item.label}</p>
                        <p className="text-lg font-semibold">{item.value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="bg-[#f5b800] text-black p-6 rounded-xl mb-6">
                    <p className="text-2xl font-bold mb-2">3-4%</p>
                    <p className="text-sm">Rendement locatif cible</p>
                  </div>

                  <div className="border-t border-white/20 pt-6">
                    <h4 className="font-semibold mb-3 text-[#f5b800]">Accès</h4>
                    <div className="space-y-2 text-sm">
                      <p>🚗 2h de Genève</p>
                      <p>🚗 40 min de Sion</p>
                      <p>⛷️ 600m des remontées</p>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-white/20">
                    <h4 className="font-semibold mb-3 text-[#f5b800]">Contact</h4>
                    <div className="flex items-start">
                      <Mail className="mr-2 flex-shrink-0 mt-1" size={18} />
                      <a href="mailto:guillaume@immoconceptsuisse.com" className="text-white hover:text-[#f5b800] transition-colors break-all">
                        guillaume@immoconceptsuisse.com
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#1e5a7a] to-[#15475c] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Intéressé par cet investissement ?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Demandez le dossier complet avec plans, grille de prix et projections locatives.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/#contact')}
              size="lg"
              className="bg-[#f5b800] hover:bg-[#d9a400] text-black font-semibold px-8 py-6 text-lg"
            >
              Nous contacter
            </Button>
            <Button
              onClick={() => window.open('mailto:guillaume@immoconceptsuisse.com', '_blank')}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#1e5a7a] font-semibold px-8 py-6 text-lg"
            >
              Envoyer un email
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};